import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ScheduleCard, StatusPill, Meta, MapPin, Users, CheckCircle2 } from "@/components/ScheduleCard";

const fmt = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })} at ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

// Why a student can't book an offering (empty string = bookable).
const blockReason = (o, user) => {
  if (o.my_booking_id) return "";
  const enrolled = (o.enrolled_student_ids || []).length || o.enrolled_count || 0;
  if (o.max_spaces && enrolled >= o.max_spaces) return "Session full";
  if (!o.is_assessment && user?.assessment_status !== "completed") {
    const a = user?.assessment_booking_start;
    if (!a) return "Assessment needed first";
    const aEnd = new Date(new Date(a).getTime() + (user.assessment_booking_duration || 60) * 60000);
    if (new Date(o.start) < aEnd) return "Before your assessment";
  }
  return "";
};

function MySchedule() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/me/sessions?limit=50").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  const ctl = useListControls(items, { name: (s) => s.title, fields: (s) => [s.title, s.coach_name, s.venue], initialSort: "default" });
  if (!items) return <div className="text-sm text-[#A3A3A3]">Loading…</div>;
  if (!items.length) return <div className="text-sm text-[#A3A3A3]" data-testid="my-schedule-empty">No upcoming sessions yet — find one in Book a Session.</div>;
  return (
    <div className="space-y-4">
    <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search your sessions…" testid="my-schedule" />
    <div className="grid gap-3 md:grid-cols-2" data-testid="my-schedule-list">
      {!ctl.list.length && <div className="text-sm text-[#737373]">No sessions match your search.</div>}
      {ctl.list.map((s) => (
        <ScheduleCard key={s.booking_id} to={`/session/${s.booking_id}`} tone="green" title={s.title} when={s.when || fmt(s.start)}
          testid={`my-schedule-card-${s.booking_id}`}
          meta={[s.coach_name && <Meta key="c" icon={Users}>{s.coach_name}</Meta>, s.venue && <Meta key="v" icon={MapPin}>{s.venue}</Meta>].filter(Boolean)}
          pills={[
            <StatusPill key="b" tone="green" testid={`my-schedule-booked-${s.booking_id}`}>Booked</StatusPill>,
            s.payment_status === "awaiting_payment" && <StatusPill key="p" tone="amber">Payment due</StatusPill>,
          ].filter(Boolean)} />
      ))}
    </div>
    </div>
  );
}

function BookTab() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/offerings").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const now = new Date();
  const open = (items || []).filter((o) => !o.is_package && o.start && new Date(o.start) > now && !["cancelled", "completed"].includes(o.status))
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const ctl = useListControls(open, { name: (o) => o.title, fields: (o) => [o.title, o.coach_name, o.venue], initialSort: "default" });
  if (!items) return <div className="text-sm text-[#A3A3A3]">Loading…</div>;
  const list = ctl.list;
  if (!open.length) return <div className="text-sm text-[#A3A3A3]" data-testid="book-empty">No sessions open right now — check back soon or request one.</div>;
  return (
    <div className="space-y-4">
    <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search sessions, coaches or venues…" testid="book" />
    <div className="grid gap-3 md:grid-cols-2" data-testid="book-list">
      {!list.length && <div className="text-sm text-[#737373]">No sessions match your search.</div>}
      {list.map((o) => {
        const booked = !!o.my_booking_id;
        const reason = blockReason(o, user);
        const enrolled = (o.enrolled_student_ids || []).length || o.enrolled_count || 0;
        const left = Math.max(0, (o.max_spaces || 0) - enrolled);
        return (
          <ScheduleCard key={o.id} to={booked ? `/session/${o.my_booking_id}` : `/offerings#offering-${o.id}`}
            tone={booked ? "green" : reason ? "muted" : "amber"} title={o.title} when={`${fmt(o.start)} · ${o.duration_min || 60} min`}
            dim={!!reason} testid={`book-card-${o.id}`}
            meta={[o.coach_name && <Meta key="c" icon={Users}>{o.coach_name}</Meta>, o.venue && <Meta key="v" icon={MapPin}>{o.venue}</Meta>].filter(Boolean)}
            pills={[
              booked && <span key="b" className="inline-flex items-center gap-1 text-xs font-bold text-[#22C55E]" data-testid={`book-booked-${o.id}`}><CheckCircle2 className="w-4 h-4" /> Booked</span>,
              !booked && reason && <StatusPill key="r" tone="muted" testid={`book-unavailable-${o.id}`}>Unavailable · {reason}</StatusPill>,
              !booked && !reason && <StatusPill key="o" tone="amber" testid={`book-open-${o.id}`}>{left} space{left === 1 ? "" : "s"} left{Number(o.price_per_player) > 0 ? ` · £${o.price_per_player}` : " · Free"}</StatusPill>,
            ].filter(Boolean)} />
        );
      })}
    </div>
    </div>
  );
}

export default function StudentSchedule() {
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "book" ? "book" : "mine"));
  const tabs = [["mine", "My Schedule"], ["book", "Book a Session"]];
  return (
    <div className="max-w-5xl space-y-8" data-testid="student-schedule">
      <header>
        <div className="label-mono mb-2 text-[#CCFF00]">SCHEDULE</div>
        <h1 className="h-display text-4xl sm:text-5xl tracking-tighter">{tab === "mine" ? "You're booked in." : "Find your next session."}</h1>
      </header>
      <div className="inline-flex p-1 bg-[#111] border border-[#1F1F1F] rounded-full" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`schedule-tab-${k}`}
            className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.12em] transition-colors ${tab === k ? "bg-[#CCFF00] text-black" : "text-[#A3A3A3] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "mine" ? <MySchedule /> : <BookTab />}
    </div>
  );
}
